import React, { useState, useContext, useEffect } from 'react';
import { Form, Input, Button, Card, message, Select, InputNumber, Space, Typography, Divider, Modal } from 'antd';
import { PlusOutlined, MinusCircleOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getAllPosts, savePost, deletePost } from '../../api/mockData'; // 使用 getAllPosts 而不是 posts
import './WritePost.css';

const { Title } = Typography;
const { TextArea } = Input;

const WritePost = () => {
  const { user } = useContext(AuthContext);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams(); // 获取文章ID，如果是编辑模式

  const [selectedTags, setSelectedTags] = useState([]);
  const [editMode, setEditMode] = useState(!!id); // 是否为编辑模式

  // 将useEffect移到所有条件判断之前，确保它始终以相同顺序被调用
  useEffect(() => {
    if (editMode && id) {
      // 使用 getAllPosts 获取所有文章（包括 localStorage 中的）
      const allPosts = getAllPosts();
      const currentPost = allPosts.find(post => post.id === id);
      if (currentPost) {
        form.setFieldsValue({
          title: currentPost.title,
          excerpt: currentPost.excerpt,
          content: currentPost.content,
          readTime: parseInt(currentPost.readTime) || 5,
        });
        setSelectedTags(currentPost.tags || []);
      }
    }
  }, [editMode, id, form]);

  // 检查是否为qmau用户，如果不是则不允许访问
  if (!user) {
    navigate('/auth');
    return null;
  }

  if (user.name !== 'qmau') {
    return (
      <div className="write-post-page">
        <Card className="write-post-card" style={{ textAlign: 'center' }}>
          <Title level={3}>权限不足</Title>
          <Typography.Paragraph>只有Qmau可以发布文章。</Typography.Paragraph>
          <Button onClick={() => navigate('/')}>返回首页</Button>
        </Card>
      </div>
    );
  }

  // 如果是编辑模式，获取当前文章数据
  const allPosts = getAllPosts();
  const currentPost = editMode ? allPosts.find(post => post.id === id) : null;

  const handleTagChange = (value) => {
    setSelectedTags(value);
  };

  const handleSubmit = async (values) => {
    if (!values.title || !values.content) {
      message.error('标题和内容不能为空');
      return;
    }

    const newPost = {
      id: editMode ? id : Date.now().toString(),
      title: values.title,
      excerpt: values.excerpt || values.content.substring(0, 100) + '...',
      content: values.content,
      author: user.name,
      authorId: user.id,
      date: editMode ? currentPost.date : new Date().toLocaleString('sv-SE'),
      tags: selectedTags,
      readTime: values.readTime ? `${values.readTime} min read` : '5 min read',
      views: editMode ? currentPost.views : 0,
      likes: editMode ? currentPost.likes : 0
    };

    try {
      // 保存文章
      await savePost(newPost);
      
      if (editMode) {
        message.success('文章更新成功！');
      } else {
        message.success('文章发布成功！');
      }
      
      navigate('/');
    } catch (error) {
      message.error(editMode ? '更新文章失败' : '发布文章失败');
    }
  };

  const handleDelete = async () => {
    if (!editMode) return;

    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这篇文章吗？此操作不可恢复。',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deletePost(id);
          message.success('文章删除成功！');
          navigate('/');
        } catch (error) {
          message.error('删除文章失败');
        }
      }
    });
  };

  return (
    <div className="write-post-page">
      <Card className="write-post-card">
        <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
          {editMode ? '编辑文章' : '写文章'}
        </Title>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            title: '',
            excerpt: '',
            content: '',
            readTime: 5
          }}
        >
          <Form.Item
            name="title"
            label="文章标题"
            rules={[{ required: true, message: '请输入文章标题' }]}
          >
            <Input placeholder="请输入文章标题" size="large" />
          </Form.Item>

          <Form.Item
            name="excerpt"
            label="文章摘要"
          >
            <TextArea 
              rows={3} 
              placeholder="请输入文章摘要，如果不填写将自动截取内容前100个字符" 
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="文章内容"
            rules={[{ required: true, message: '请输入文章内容' }]}
          >
            <TextArea 
              rows={15} 
              placeholder="请输入文章内容" 
              style={{ fontSize: '14px', lineHeight: '1.6' }}
            />
          </Form.Item>

          <Form.Item label="标签">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="请选择或输入标签"
              value={selectedTags}
              onChange={handleTagChange}
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Form.Item
            name="readTime"
            label="预估阅读时间（分钟）"
          >
            <InputNumber 
              min={1} 
              max={300} 
              placeholder="预估阅读时间" 
              style={{ width: '100%' }} 
            />
          </Form.Item>

          <Form.Item>
            <Space size="middle">
              <Button type="primary" htmlType="submit" size="large">
                {editMode ? '更新文章' : '发布文章'}
              </Button>
              
              {editMode && (
                <Button danger size="large" onClick={handleDelete} icon={<DeleteOutlined />}>
                  删除文章
                </Button>
              )}
              
              <Button size="large" onClick={() => navigate('/')}>
                返回
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default WritePost;